/**
 * WoDeleteDialog — the hardened work-order delete confirmation
 * (incident WO-2026-110, 2026-08-05).
 *
 * The old confirm said only "Are you sure you want to delete this work
 * order?" — no number, no name, no test counts. Combined with the button
 * sitting inside the schedule-controls cluster, a real work order with 12
 * signed-off tests was deleted by someone who believed they were clearing a
 * leftover test artifact. This dialog makes that slip impossible:
 *
 *   - names the work order, its name and its project;
 *   - states how many test rows it holds and how many are signed off;
 *   - when completed tests exist, deletion requires TYPING the work-order
 *     number before the button enables;
 *   - schedule impact (committed bookings / plans) is spelled out, and the
 *     confirm relabels to "Release schedule & delete".
 */
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography,
} from '@mui/material';
import { WarningAmberOutlined } from '@mui/icons-material';

interface Props {
  open: boolean;
  /** The human work-order number, e.g. "WO-2026-110" — the typed-confirm target. */
  workOrderId: string;
  workOrderName: string | null;
  projectId: string | null;
  testCount: number;
  completedCount: number;
  /** Human sentence about schedule impact, or null when nothing is attached. */
  scheduleNote: string | null;
  /**
   * Further consequence lines (quote link cleared, tests with entered results…)
   * — built by woDeleteNotes from the server's delete-preview.
   */
  notes?: string[];
  /** True when confirming must also release schedule (finalized plans / committed work). */
  requireRelease: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (releaseSchedule: boolean) => void;
}

export const WoDeleteDialog = ({
  open, workOrderId, workOrderName, projectId, testCount, completedCount,
  scheduleNote, notes = [], requireRelease, busy, error, onCancel, onConfirm,
}: Props) => {
  const [typed, setTyped] = useState('');
  useEffect(() => { if (open) setTyped(''); }, [open]);

  const needsTyping = completedCount > 0;
  const typedOk = !needsTyping || typed.trim().toUpperCase() === workOrderId.trim().toUpperCase();

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        Delete {workOrderId}{workOrderName ? ` — ${workOrderName}` : ''}?
      </DialogTitle>
      <DialogContent sx={{ pt: '4px !important' }}>
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 1.5, p: 1.5, mb: 1.5, fontSize: '0.875rem' }}>
          <Typography variant="body2">
            Project: <strong>{projectId ?? '—'}</strong>
          </Typography>
          <Typography variant="body2">
            Contains: <strong>{testCount} test row{testCount === 1 ? '' : 's'}
            {completedCount > 0 ? ` — ${completedCount} completed / signed off` : ''}</strong>
          </Typography>
          {scheduleNote && <Typography variant="body2">{scheduleNote}</Typography>}
          {notes.map((note) => (
            <Typography key={note} variant="body2">{note}</Typography>
          ))}
        </Box>

        {completedCount > 0 && (
          <Alert severity="error" icon={<WarningAmberOutlined />} sx={{ mb: 1.5 }}>
            This work order holds <strong>completed, signed-off test results</strong>. If you are
            cleaning up schedules or plans, this is NOT the button — deleting here removes the whole
            work order, not its schedule.
          </Alert>
        )}

        {needsTyping && (
          <>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Type the work-order number to confirm:
            </Typography>
            <TextField
              size="small" fullWidth autoFocus placeholder={workOrderId}
              value={typed} onChange={(e) => setTyped(e.target.value)}
              inputProps={{ autoComplete: 'off' }}
            />
          </>
        )}

        {error && <Alert severity="warning" sx={{ mt: 1.5 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained" color="error"
          disabled={busy || !typedOk}
          onClick={() => onConfirm(requireRelease)}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {busy ? 'Deleting…' : requireRelease ? 'Release schedule & delete' : 'Delete work order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
